import React, {
  useMemo,
  useState,
  useCallback,
  useEffect,
} from 'react';
import { CloudSyncOutlined, SettingOutlined } from '@ant-design/icons';
import { Row, Select } from 'antd';
import axios from 'axios';

import WorkflowHandle from './WorkflowHandle';
import WorkflowActionNode from './WorkflowActionNode';
import WorkflowSageIntacctPOConfigureDrawer from './WorkflowSageIntacctPOConfigureDrawer';
import useToggle from '../../common/hooks/useToggle';
import BorderlessButton from '../../common/buttons/BorderlessButton';
import { getType, updateData } from './workflowHelpers';
import { resolveFormDivisionIds } from '../formHelpers';

const ERROR_DETAILS = {
  content: (
    <div style={{ maxWidth: 300 }}>
      This step requires the following fields to be configured: <br />
      - <b>Material Table Field</b> (set via <b>Configure</b>) <br />
      - <b>Vendor Field</b> (set via <b>Configure</b>) <br />
    </div>
  ),
};

const DISABLED_TEXT = 'Add at least one Material Table Field';

const HOVER_TEXT = (
  <div>
    Create a Purchase Order in Sage Intacct from material table line items
  </div>
);

const areDivisionIdsEqual = (divisionIds1, divisionIds2) => {
  if (divisionIds1?.length !== divisionIds2?.length) return false;
  return divisionIds1?.every((id) => divisionIds2?.includes(id));
};

export default function WorkflowSageIntacctPONode({
  setDataMap,
  setElements,
  isDisplay,
  name,
  sections = [],
  divisionId,
  divisionIds = [],
  setDisableNext,
} = {}) {
  const relevantDivisionIds = resolveFormDivisionIds({ divisionIds, divisionId });

  return function _({
    id,
    draggable,
    data = {},
    disabled,
  }) {
    const {
      fieldMappings: initialFieldMappings = {},
      attachmentFolder: initialAttachmentFolder = null,
    } = data;

    const [fieldMappings, setFieldMappings] = useState(initialFieldMappings);
    const [attachmentFolders, setAttachmentFolders] = useState([]);
    const [prevDivisionIds, setPrevDivisionIds] = useState([]);
    const [
      selectedAttachmentFolder,
      setSelectedAttachmentFolder,
    ] = useState(initialAttachmentFolder);

    const {
      isToggled: isMappingDrawerVisible,
      toggle: toggleMappingDrawer,
    } = useToggle(false);

    const disableNext = useMemo(() => (
      !fieldMappings?.materialTable
      || !fieldMappings?.vendorId
    ), [fieldMappings]);

    useEffect(() => {
      if (!setDisableNext) return;
      setDisableNext((prev) => ({ ...prev, [id]: disableNext }));
    }, [setDisableNext, id, disableNext]);

    const onFolderChange = useCallback((newValue) => {
      if (!setDataMap || !id) return;
      setSelectedAttachmentFolder(newValue);
      setDataMap(updateData(id, { attachmentFolder: newValue }));
    }, [id, setDataMap]);

    const getAttachmentFolders = async () => {
      try {
        const { data: folders = [] } = await axios.get(
          '/sage/intacct/folders',
          {
            params: {
              divisionIds: relevantDivisionIds,
            },
          },
        );
        setAttachmentFolders(Array.isArray(folders) ? folders.flat() : []);
      } catch (err) {
        console.error('Error getting attachment folders', err);
        // silent fail
      }
    };

    const onFieldMappingsChange = useCallback((newMappings) => {
      if (!setDataMap || !id) return;
      setFieldMappings(newMappings);
      setDataMap(updateData(id, { fieldMappings: newMappings }));
    }, [id, setDataMap]);

    const materialTableFields = useMemo(() => (
      sections.reduce((sAcc, section) => {
        const { fields = [] } = section;
        return sAcc.concat(
          fields.reduce((fAcc, field) => {
            const { id: fieldId, configProps: { title } = {} } = field;
            const type = getType(field);
            if (type === 'table - Materials') {
              fAcc.push({ label: title, value: fieldId });
            }
            return fAcc;
          }, []),
        );
      }, [])
    ), [sections]);

    const errorDetails = !fieldMappings?.materialTable
      || !fieldMappings?.vendorId
      ? ERROR_DETAILS
      : undefined;

    if (relevantDivisionIds?.length && !areDivisionIdsEqual(relevantDivisionIds, prevDivisionIds)) {
      setPrevDivisionIds(relevantDivisionIds);
      getAttachmentFolders();
    }

    return (
      <div style={{ position: 'relative' }}>
        <WorkflowActionNode
          title="Create PO in Sage Intacct"
          Icon={CloudSyncOutlined}
          draggable={draggable}
          onNodeUpdate={(...args) => {
            setElements(...args);
            if (setDisableNext) {
              setDisableNext((prev) => ({ ...prev, [id]: false }));
            }
          }}
          isDisplay={isDisplay || disabled}
          errorPopover={errorDetails}
          hover={disabled ? DISABLED_TEXT : HOVER_TEXT}
          style={disabled ? { opacity: 0.7 } : {}}
          type="sageIntacctPO"
          id={id}
        >
          {!draggable && (
            <div>
              <WorkflowHandle type="target" position="top" disabled={isDisplay} />
              <Row style={{ marginTop: 10 }}>
                Select Attachment Folder
              </Row>
              <Row style={{ margin: '10px 0px' }}>
                <Select
                  onChange={onFolderChange}
                  value={selectedAttachmentFolder}
                  showSearch
                  optionFilterProp="label"
                  options={attachmentFolders.map(({ key, id: label }) => ({ value: key, label }))}
                />
              </Row>
              <Row style={{ margin: '20px 0px' }}>
                <BorderlessButton
                  title="Configure"
                  style={{ margin: 5 }}
                  iconNode={<SettingOutlined />}
                  onClick={toggleMappingDrawer}
                />
              </Row>
            </div>
          )}
        </WorkflowActionNode>
        <WorkflowSageIntacctPOConfigureDrawer
          visible={isMappingDrawerVisible}
          onClose={toggleMappingDrawer}
          onSubmit={toggleMappingDrawer}
          sourceName={name}
          sourceSections={sections}
          fieldMappings={fieldMappings}
          onFieldMappingsChange={onFieldMappingsChange}
          materialTableFields={materialTableFields}
        />
      </div>
    );
  };
}
